import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import AdminLayout from '../../components/admin/AdminLayout';
import { useAuthStore } from '../../stores/authStore';
import api from '../../utils/api';
import { formatDate } from '../../utils/helpers';

export default function ReviewSubmissions() {
    const { user } = useAuthStore();
    const isContributor = user?.role === 'contributor';
    
    const [submissions, setSubmissions] = useState([]);
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState('pending');
    const [selectedSubmission, setSelectedSubmission] = useState(null);
    const [showModal, setShowModal] = useState(false);
    const [modalAction, setModalAction] = useState('');
    const [rejectionReason, setRejectionReason] = useState('');
    const [adminNotes, setAdminNotes] = useState('');
    const [processing, setProcessing] = useState(false);

    useEffect(() => {
        loadData();
    }, [filter]);

    const loadData = async () => {
        try {
            setLoading(true);
            
            if (isContributor) {
                // Contributeur : charger seulement ses soumissions
                const [submissionsRes, statsRes] = await Promise.all([
                    api.get('/submissions/my'),
                    api.get('/submissions/stats')
                ]);
                
                // Filtrer localement si nécessaire
                let filtered = submissionsRes.data.submissions;
                console.log('📋 Toutes mes soumissions:', filtered);
                console.log('🔍 Soumissions rejetées:', filtered.filter(s => s.status === 'rejected'));
                if (filter !== 'all') {
                    filtered = filtered.filter(s => s.status === filter);
                }
                
                setSubmissions(filtered);
                setStats(statsRes.data.stats);
            } else {
                // Admin/Rédacteur : charger toutes les soumissions
                const [submissionsRes, statsRes] = await Promise.all([
                    api.get(`/admin/submissions?status=${filter === 'all' ? '' : filter}`),
                    api.get('/admin/submissions/stats')
                ]);

                setSubmissions(submissionsRes.data.submissions);
                setStats(statsRes.data.stats);
            }
        } catch (error) {
            console.error('Erreur chargement:', error);
        } finally {
            setLoading(false);
        }
    };

    const openModal = (submission, action) => {
        setSelectedSubmission(submission);
        setModalAction(action);
        setRejectionReason('');
        setAdminNotes(submission.admin_notes || '');
        setShowModal(true);
    };

    const closeModal = () => {
        setShowModal(false);
        setSelectedSubmission(null);
        setRejectionReason('');
        setAdminNotes('');
    };

    const handleApprove = async () => {
        if (!selectedSubmission) return;
        
        setProcessing(true);
        try {
            await api.put(`/admin/submissions/${selectedSubmission.id}/approve`, {
                admin_notes: adminNotes
            });
            console.log('✅ Soumission approuvée');
            closeModal();
            loadData();
        } catch (error) {
            console.error('❌ Erreur approbation:', error);
            alert(error.response?.data?.error || 'Erreur lors de l\'approbation');
        } finally {
            setProcessing(false);
        }
    };

    const handleReject = async () => {
        if (!selectedSubmission || !rejectionReason.trim()) {
            alert('Veuillez indiquer une raison pour le rejet');
            return;
        }
        
        setProcessing(true);
        try {
            await api.put(`/admin/submissions/${selectedSubmission.id}/reject`, {
                rejection_reason: rejectionReason,
                admin_notes: adminNotes
            });
            console.log('❌ Soumission rejetée');
            closeModal();
            loadData();
        } catch (error) {
            console.error('❌ Erreur rejet:', error);
            alert(error.response?.data?.error || 'Erreur lors du rejet');
        } finally {
            setProcessing(false);
        }
    };

    const getStatusBadge = (status) => {
        const badges = {
            pending: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
            approved: 'bg-green-500/20 text-green-400 border-green-500/30',
            rejected: 'bg-red-500/20 text-red-400 border-red-500/30',
            published: 'bg-blue-500/20 text-blue-400 border-blue-500/30'
        };
        const labels = {
            pending: 'En attente',
            approved: 'Approuvé',
            rejected: 'Rejeté',
            published: 'Publié'
        };
        return (
            <span className={`px-3 py-1 rounded-full text-xs font-medium border ${badges[status]}`}>
                {labels[status]}
            </span>
        );
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-96">
                <div className="text-center">
                    <i className="fas fa-spinner fa-spin text-4xl text-yellow-400 mb-4"></i>
                    <p className="text-text-secondary">Chargement...</p>
                </div>
            </div>
        );
    }

    return (
        <AdminLayout>
            <div className="p-8">
                <div className="mb-8">
                    <h1 className="text-3xl font-bold text-white mb-2">
                        {isContributor ? 'Mes soumissions' : 'Soumissions des contributeurs'}
                    </h1>
                    <p className="text-gray-400">
                        {isContributor 
                            ? 'Gérez vos articles soumis et suivez leur statut' 
                            : 'Validez et gérez les articles soumis par les contributeurs'}
                    </p>
                </div>

                <div className="space-y-6">
            {/* Header Stats */}
            <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
                <div className="bg-surface rounded-lg p-4 border border-gray-700/30">
                    <p className="text-text-secondary text-sm">Total</p>
                    <p className="text-2xl font-bold text-text">{stats?.total || 0}</p>
                </div>
                <div className="bg-surface rounded-lg p-4 border border-yellow-500/30">
                    <p className="text-text-secondary text-sm">En attente</p>
                    <p className="text-2xl font-bold text-yellow-400">{stats?.pending || 0}</p>
                </div>
                <div className="bg-surface rounded-lg p-4 border border-green-500/30">
                    <p className="text-text-secondary text-sm">Approuvés</p>
                    <p className="text-2xl font-bold text-green-400">{stats?.approved || 0}</p>
                </div>
                <div className="bg-surface rounded-lg p-4 border border-blue-500/30">
                    <p className="text-text-secondary text-sm">Publiés</p>
                    <p className="text-2xl font-bold text-blue-400">{stats?.published || 0}</p>
                </div>
                <div className="bg-surface rounded-lg p-4 border border-red-500/30">
                    <p className="text-text-secondary text-sm">Rejetés</p>
                    <p className="text-2xl font-bold text-red-400">{stats?.rejected || 0}</p>
                </div>
            </div>

            {/* Filters */}
            <div className="bg-surface rounded-lg p-4 border border-gray-700/30">
                <div className="flex flex-wrap gap-2">
                    <button
                        onClick={() => setFilter('pending')}
                        className={`px-4 py-2 rounded-lg transition-colors ${
                            filter === 'pending'
                                ? 'bg-yellow-400 text-gray-900'
                                : 'bg-background text-text-secondary hover:bg-background/80'
                        }`}
                    >
                        En attente ({stats?.pending || 0})
                    </button>
                    <button
                        onClick={() => setFilter('approved')}
                        className={`px-4 py-2 rounded-lg transition-colors ${
                            filter === 'approved'
                                ? 'bg-yellow-400 text-gray-900'
                                : 'bg-background text-text-secondary hover:bg-background/80'
                        }`}
                    >
                        Approuvés ({stats?.approved || 0})
                    </button>
                    <button
                        onClick={() => setFilter('published')}
                        className={`px-4 py-2 rounded-lg transition-colors ${
                            filter === 'published'
                                ? 'bg-yellow-400 text-gray-900'
                                : 'bg-background text-text-secondary hover:bg-background/80'
                        }`}
                    >
                        Publiés ({stats?.published || 0})
                    </button>
                    <button
                        onClick={() => setFilter('rejected')}
                        className={`px-4 py-2 rounded-lg transition-colors ${
                            filter === 'rejected'
                                ? 'bg-yellow-400 text-gray-900'
                                : 'bg-background text-text-secondary hover:bg-background/80'
                        }`}
                    >
                        Rejetés ({stats?.rejected || 0})
                    </button>
                    <button
                        onClick={() => setFilter('all')}
                        className={`px-4 py-2 rounded-lg transition-colors ${
                            filter === 'all'
                                ? 'bg-yellow-400 text-gray-900'
                                : 'bg-background text-text-secondary hover:bg-background/80'
                        }`}
                    >
                        Tous ({stats?.total || 0})
                    </button>
                </div>
            </div>

            {/* Submissions List */}
            <div className="bg-surface rounded-lg shadow-lg border border-gray-700/30 overflow-hidden">
                <div className="px-6 py-4 border-b border-gray-700/30">
                    <h2 className="text-xl font-semibold text-text">
                        <i className="fas fa-tasks mr-2 text-yellow-400"></i>
                        Soumissions des contributeurs
                    </h2>
                </div>

                <div className="overflow-x-auto">
                    {submissions.length === 0 ? (
                        <div className="text-center py-12">
                            <i className="fas fa-inbox text-6xl text-gray-600 mb-4"></i>
                            <p className="text-text-secondary">Aucune soumission</p>
                        </div>
                    ) : (
                        <table className="min-w-full divide-y divide-gray-700/30">
                            <thead className="bg-background">
                                <tr>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-text-secondary uppercase tracking-wider">
                                        Titre
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-text-secondary uppercase tracking-wider">
                                        Contributeur
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-text-secondary uppercase tracking-wider">
                                        Catégorie
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-text-secondary uppercase tracking-wider">
                                        Statut
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-text-secondary uppercase tracking-wider">
                                        Date
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-text-secondary uppercase tracking-wider">
                                        Récompense
                                    </th>
                                    <th className="px-6 py-3 text-right text-xs font-medium text-text-secondary uppercase tracking-wider">
                                        Actions
                                    </th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-700/30">
                                {submissions.map((submission) => (
                                    <tr key={submission.id} className="hover:bg-background/50 transition-colors">
                                        <td className="px-6 py-4">
                                            <div className="text-text font-medium">{submission.title}</div>
                                            <div className="text-sm text-text-secondary">
                                                {submission.reading_time} min de lecture
                                            </div>
                                        </td>
                                        <td className="px-6 py-4">
                                            <div className="text-text">{submission.contributor_name}</div>
                                            <div className="text-xs text-text-secondary">{submission.contributor_email}</div>
                                        </td>
                                        <td className="px-6 py-4">
                                            {submission.category_name ? (
                                                <span 
                                                    className="px-2 py-1 rounded text-xs font-medium"
                                                    style={{ 
                                                        backgroundColor: `${submission.category_color}20`,
                                                        color: submission.category_color
                                                    }}
                                                >
                                                    {submission.category_name}
                                                </span>
                                            ) : (
                                                <span className="text-text-secondary text-sm">-</span>
                                            )}
                                        </td>
                                        <td className="px-6 py-4">
                                            {getStatusBadge(submission.status)}
                                        </td>
                                        <td className="px-6 py-4 text-sm text-text-secondary">
                                            {formatDate(submission.submitted_at)}
                                        </td>
                                        <td className="px-6 py-4">
                                            <span className="text-yellow-400 font-semibold">
                                                {submission.reward_amount}€
                                            </span>
                                            {submission.reward_credited && (
                                                <i className="fas fa-check-circle text-green-400 ml-2" title="Crédit ajouté"></i>
                                            )}
                                        </td>
                                        <td className="px-6 py-4 text-right">
                                            <div className="flex items-center justify-end space-x-2">
                                                <a
                                                    href={`#preview-${submission.id}`}
                                                    onClick={(e) => {
                                                        e.preventDefault();
                                                        setSelectedSubmission(submission);
                                                        setShowModal(true);
                                                        setModalAction('view');
                                                    }}
                                                    className="text-blue-400 hover:text-blue-500"
                                                    title="Prévisualiser"
                                                >
                                                    <i className="fas fa-eye"></i>
                                                </a>
                                                {submission.status === 'pending' && !isContributor && (
                                                    <>
                                                        {submission.article_id ? (
                                                            <Link
                                                                to={`/admin/articles/edit/${submission.article_id}`}
                                                                className="text-blue-400 hover:text-blue-500"
                                                                title="Modifier avant approbation"
                                                            >
                                                                <i className="fas fa-edit"></i>
                                                            </Link>
                                                        ) : (
                                                            <span 
                                                                className="text-gray-600 cursor-not-allowed" 
                                                                title="Soumission créée avant la mise à jour - modification non disponible"
                                                            >
                                                                <i className="fas fa-edit"></i>
                                                            </span>
                                                        )}
                                                        <button
                                                            onClick={() => openModal(submission, 'approve')}
                                                            className="text-green-400 hover:text-green-500"
                                                            title="Approuver"
                                                        >
                                                            <i className="fas fa-check"></i>
                                                        </button>
                                                        <button
                                                            onClick={() => openModal(submission, 'reject')}
                                                            className="text-red-400 hover:text-red-500"
                                                            title="Rejeter"
                                                        >
                                                            <i className="fas fa-times"></i>
                                                        </button>
                                                    </>
                                                )}
                                                {submission.status === 'rejected' && isContributor && (
                                                    <>
                                                        {submission.article_id ? (
                                                            <Link
                                                                to={`/contributor/edit/${submission.article_id}`}
                                                                className="text-yellow-400 hover:text-yellow-500"
                                                                title="Modifier et resoumettre"
                                                            >
                                                                <i className="fas fa-edit"></i>
                                                            </Link>
                                                        ) : (
                                                            <span 
                                                                className="text-gray-600 cursor-not-allowed"
                                                                title="Cette soumission a été créée avant la mise à jour. Veuillez créer un nouvel article."
                                                            >
                                                                <i className="fas fa-edit"></i>
                                                            </span>
                                                        )}
                                                    </>
                                                )}
                                                {submission.status === 'published' && submission.article_id && (
                                                    <Link
                                                        to={`/admin/articles/edit/${submission.article_id}`}
                                                        className="text-yellow-400 hover:text-yellow-500"
                                                        title="Modifier l'article"
                                                    >
                                                        <i className="fas fa-edit"></i>
                                                    </Link>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>
            </div>

            {/* Modal */}
            {showModal && selectedSubmission && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                    <div className="bg-surface rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
                        <div className="p-6 border-b border-gray-700/30">
                            <div className="flex items-center justify-between">
                                <h3 className="text-2xl font-bold text-text">
                                    {modalAction === 'view' && 'Prévisualisation'}
                                    {modalAction === 'approve' && 'Approuver la soumission'}
                                    {modalAction === 'reject' && 'Rejeter la soumission'}
                                </h3>
                                <button
                                    onClick={closeModal}
                                    className="text-text-secondary hover:text-red-400 transition-colors"
                                >
                                    <i className="fas fa-times text-2xl"></i>
                                </button>
                            </div>
                        </div>

                        <div className="p-6 space-y-6">
                            {/* Preview */}
                            <div>
                                <h4 className="text-xl font-bold text-text mb-2">{selectedSubmission.title}</h4>
                                <p className="text-text-secondary text-sm mb-4">{selectedSubmission.short_description}</p>
                                {selectedSubmission.featured_image && (
                                    <img 
                                        src={selectedSubmission.featured_image} 
                                        alt={selectedSubmission.title}
                                        className="w-full rounded-lg mb-4"
                                    />
                                )}
                                <div 
                                    className="prose prose-invert max-w-none"
                                    dangerouslySetInnerHTML={{ __html: selectedSubmission.content }}
                                />
                            </div>

                            {/* Actions */}
                            {modalAction === 'approve' && (
                                <div className="space-y-4">
                                    <div>
                                        <label className="block text-sm font-medium text-text mb-2">
                                            Notes administratives (optionnel)
                                        </label>
                                        <textarea
                                            value={adminNotes}
                                            onChange={(e) => setAdminNotes(e.target.value)}
                                            rows="3"
                                            className="w-full px-4 py-2 bg-background border border-gray-700 rounded-lg text-text focus:outline-none focus:ring-2 focus:ring-green-400"
                                            placeholder="Notes internes..."
                                        />
                                    </div>
                                    <div className="flex items-center justify-end space-x-3">
                                        <button
                                            onClick={closeModal}
                                            className="px-6 py-3 bg-gray-700 text-text rounded-lg hover:bg-gray-600 transition-colors"
                                        >
                                            Annuler
                                        </button>
                                        <button
                                            onClick={handleApprove}
                                            disabled={processing}
                                            className="px-6 py-3 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors disabled:opacity-50"
                                        >
                                            {processing ? (
                                                <>
                                                    <i className="fas fa-spinner fa-spin mr-2"></i>
                                                    Traitement...
                                                </>
                                            ) : (
                                                <>
                                                    <i className="fas fa-check mr-2"></i>
                                                    Approuver et publier
                                                </>
                                            )}
                                        </button>
                                    </div>
                                </div>
                            )}

                            {modalAction === 'reject' && (
                                <div className="space-y-4">
                                    <div>
                                        <label className="block text-sm font-medium text-text mb-2">
                                            Raison du rejet *
                                        </label>
                                        <textarea
                                            value={rejectionReason}
                                            onChange={(e) => setRejectionReason(e.target.value)}
                                            rows="3"
                                            className="w-full px-4 py-2 bg-background border border-red-500/30 rounded-lg text-text focus:outline-none focus:ring-2 focus:ring-red-400"
                                            placeholder="Expliquez pourquoi cet article est rejeté (le contributeur verra ce message)..."
                                            required
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-text mb-2">
                                            Notes administratives (optionnel)
                                        </label>
                                        <textarea
                                            value={adminNotes}
                                            onChange={(e) => setAdminNotes(e.target.value)}
                                            rows="2"
                                            className="w-full px-4 py-2 bg-background border border-gray-700 rounded-lg text-text focus:outline-none focus:ring-2 focus:ring-yellow-400"
                                            placeholder="Notes internes..."
                                        />
                                    </div>
                                    <div className="flex items-center justify-end space-x-3">
                                        <button
                                            onClick={closeModal}
                                            className="px-6 py-3 bg-gray-700 text-text rounded-lg hover:bg-gray-600 transition-colors"
                                        >
                                            Annuler
                                        </button>
                                        <button
                                            onClick={handleReject}
                                            disabled={processing || !rejectionReason.trim()}
                                            className="px-6 py-3 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors disabled:opacity-50"
                                        >
                                            {processing ? (
                                                <>
                                                    <i className="fas fa-spinner fa-spin mr-2"></i>
                                                    Traitement...
                                                </>
                                            ) : (
                                                <>
                                                    <i className="fas fa-times mr-2"></i>
                                                    Rejeter
                                                </>
                                            )}
                                        </button>
                                    </div>
                                </div>
                            )}

                            {modalAction === 'view' && (
                                <div className="flex items-center justify-end space-x-3">
                                    <button
                                        onClick={closeModal}
                                        className="px-6 py-3 bg-gray-700 text-text rounded-lg hover:bg-gray-600 transition-colors"
                                    >
                                        Fermer
                                    </button>
                                    {selectedSubmission.status === 'pending' && (
                                        <>
                                            <button
                                                onClick={() => setModalAction('approve')}
                                                className="px-6 py-3 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors"
                                            >
                                                <i className="fas fa-check mr-2"></i>
                                                Approuver
                                            </button>
                                            <button
                                                onClick={() => setModalAction('reject')}
                                                className="px-6 py-3 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors"
                                            >
                                                <i className="fas fa-times mr-2"></i>
                                                Rejeter
                                            </button>
                                        </>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}
            </div> {/* Fin space-y-6 */}
            </div> {/* Fin p-8 */}
        </AdminLayout>
    );
}
